import React, { useState, useRef, useEffect } from 'react';
import {
  Settings,
  Type,
  Palette,
  Move,
  ChevronDown,
  ChevronRight,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Bold,
  Italic,
  Underline,
  Image as ImageIcon,
  Square,
  Layout,
  Upload,
  Loader2
} from 'lucide-react';
import toast from 'react-hot-toast';
import { storageService } from '@/services/storage.service';
import {
  CanvasElement,
  CanvasElementType,
  TextCanvasElement,
  VariableCanvasElement,
  ImageCanvasElement,
  ShapeCanvasElement,
  LineCanvasElement,
  TextStyle,
  TextAlign,
  FONT_FAMILIES,
  CanvasConfig
} from '@/types/certificate-template.types';
import ColorPalette from './ColorPalette';

interface PropertiesPanelProps {
  selectedElement: CanvasElement | null;
  canvasConfig: CanvasConfig;
  onUpdateElement: (id: string, updates: Partial<CanvasElement>) => void;
  onUpdateCanvas: (config: Partial<CanvasConfig>) => void;
}

interface CollapsibleSectionProps {
  title: string;
  icon: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

function CollapsibleSection({ title, icon, defaultOpen = true, children }: CollapsibleSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border-b">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-gray-400">{icon}</span>
          <span className="text-sm font-medium text-gray-700">{title}</span>
        </div>
        {isOpen ? (
          <ChevronDown className="w-4 h-4 text-gray-400" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-400" />
        )}
      </button>
      {isOpen && <div className="px-4 pb-4 space-y-3">{children}</div>}
    </div>
  );
}

function InputLabel({ children }: { children: React.ReactNode }) {
  return <label className="block text-xs font-medium text-gray-500 mb-1">{children}</label>;
}

/** Numeric input that allows free typing — commits value on blur or Enter */
function NumericInput({
  value,
  onChange,
  fallback = 0,
  step,
  min,
  max,
  className,
  title,
  placeholder,
  useFloat
}: {
  value: number;
  onChange: (v: number) => void;
  fallback?: number;
  step?: string;
  min?: number;
  max?: number;
  className?: string;
  title?: string;
  placeholder?: string;
  useFloat?: boolean;
}) {
  const [localValue, setLocalValue] = useState(String(value));
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!isFocused) setLocalValue(String(value));
  }, [value, isFocused]);

  const commit = () => {
    const parsed = useFloat ? parseFloat(localValue) : parseInt(localValue);
    const final = isNaN(parsed) ? fallback : parsed;
    const clamped = Math.max(min ?? -Infinity, Math.min(max ?? Infinity, final));
    onChange(clamped);
    setLocalValue(String(clamped));
  };

  return (
    <input
      type="number"
      value={isFocused ? localValue : String(value)}
      onFocus={(e) => { setIsFocused(true); setLocalValue(String(value)); e.target.select(); }}
      onBlur={() => { setIsFocused(false); commit(); }}
      onChange={(e) => {
        setLocalValue(e.target.value);
        const parsed = useFloat ? parseFloat(e.target.value) : parseInt(e.target.value);
        if (!isNaN(parsed)) onChange(Math.max(min ?? -Infinity, Math.min(max ?? Infinity, parsed)));
      }}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.currentTarget.blur(); } }}
      step={step}
      min={min}
      max={max}
      className={className || 'w-full px-2 py-1.5 text-sm border rounded focus:ring-2 focus:ring-blue-500'}
      title={title}
      placeholder={placeholder}
    />
  );
}

/** Image field with upload to S3 + URL input */
function ImageUploadField({ src, onChangeSrc }: { src: string; onChangeSrc: (url: string) => void }) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate
    const validation = storageService.validateFile(file);
    if (!validation.valid) {
      toast.error(validation.error || 'Invalid file');
      return;
    }

    // Check if image
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file (JPG, PNG, GIF, WebP)');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const result = await storageService.uploadFile(file, {
        onProgress: (progress) => setUploadProgress(progress.percentage)
      });
      onChangeSrc(result.url);
      toast.success('Image uploaded successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to upload image');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-2">
      {/* Preview */}
      {src && (
        <div className="w-full h-24 bg-gray-100 rounded-lg border overflow-hidden flex items-center justify-center">
          <img src={src} alt="Preview" className="max-w-full max-h-full object-contain" />
        </div>
      )}

      {/* Upload button */}
      <button
        onClick={() => fileInputRef.current?.click()}
        disabled={isUploading}
        className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
      >
        {isUploading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Uploading {uploadProgress}%
          </>
        ) : (
          <>
            <Upload className="w-4 h-4" />
            Upload Image
          </>
        )}
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Progress bar */}
      {isUploading && (
        <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-blue-600 rounded-full transition-all duration-300"
            style={{ width: `${uploadProgress}%` }}
          />
        </div>
      )}

      {/* URL input */}
      <div>
        <InputLabel>Or paste URL</InputLabel>
        <input
          type="text"
          value={src}
          onChange={(e) => onChangeSrc(e.target.value)}
          placeholder="https://..."
          className="w-full px-2 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500"
        />
      </div>
    </div>
  );
}

export default function PropertiesPanel({
  selectedElement,
  canvasConfig,
  onUpdateElement,
  onUpdateCanvas
}: PropertiesPanelProps) {
  const [activeTab, setActiveTab] = useState<'element' | 'canvas'>('element');


  const updateElementStyle = (styleUpdates: Partial<TextStyle>) => {
    if (!selectedElement) return;
    if (
      selectedElement.type === CanvasElementType.TEXT ||
      selectedElement.type === CanvasElementType.VARIABLE
    ) {
      const el = selectedElement as TextCanvasElement | VariableCanvasElement;
      onUpdateElement(selectedElement.id, {
        style: { ...el.style, ...styleUpdates }
      } as any);
    }
  };

  const updatePosition = (key: 'x' | 'y' | 'width' | 'height' | 'rotation', value: number) => {
    if (!selectedElement) return;
    onUpdateElement(selectedElement.id, {
      position: { ...selectedElement.position, [key]: value }
    });
  };

  // Canvas properties panel
  const renderCanvasProperties = () => (
    <>
      <CollapsibleSection title="Canvas Size" icon={<Layout className="w-4 h-4" />}>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <InputLabel>Width (px)</InputLabel>
            <NumericInput
              value={canvasConfig.width}
              onChange={(v) => onUpdateCanvas({ width: v })}
              fallback={842} min={100}
            />
          </div>
          <div>
            <InputLabel>Height (px)</InputLabel>
            <NumericInput
              value={canvasConfig.height}
              onChange={(v) => onUpdateCanvas({ height: v })}
              fallback={595} min={100}
            />
          </div>
        </div>
        <div className="mt-2">
          <button
            onClick={() => onUpdateCanvas({ width: 842, height: 595, orientation: 'landscape' })}
            className={`w-full px-3 py-1.5 text-xs rounded border transition-colors ${
              canvasConfig.orientation === 'landscape'
                ? 'bg-blue-50 border-blue-500 text-blue-700'
                : 'hover:bg-gray-50'
            }`}
          >
            Landscape
          </button>
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Background" icon={<Palette className="w-4 h-4" />}>
        <div className="space-y-3">
          <div>
            <InputLabel>Type</InputLabel>
            <select
              value={canvasConfig.background.type}
              onChange={(e) =>
                onUpdateCanvas({
                  background: { ...canvasConfig.background, type: e.target.value as any }
                })
              }
              className="w-full px-2 py-1.5 text-sm border rounded focus:ring-2 focus:ring-blue-500"
            >
              <option value="solid">Solid Color</option>
              <option value="gradient">Gradient</option>
              <option value="image">Image</option>
            </select>
          </div>

          {canvasConfig.background.type === 'solid' && (
            <ColorPalette
              label="Color"
              value={canvasConfig.background.color || '#FFFFFF'}
              onChange={(color) =>
                onUpdateCanvas({
                  background: { type: 'solid', color }
                })
              }
            />
          )}

          {canvasConfig.background.type === 'gradient' && (
            <div className="space-y-3">
              <ColorPalette
                label="Pick a gradient"
                value={canvasConfig.background.gradient?.colors?.[0]?.color || '#667eea'}
                onChange={(color) =>
                  onUpdateCanvas({
                    background: {
                      type: 'gradient',
                      gradient: {
                        type: 'linear',
                        angle: canvasConfig.background.gradient?.angle || 135,
                        colors: [
                          { color, position: 0 },
                          { color: canvasConfig.background.gradient?.colors?.[1]?.color || '#764ba2', position: 100 }
                        ]
                      }
                    }
                  })
                }
                showGradients
                onGradientChange={(g) =>
                  onUpdateCanvas({
                    background: {
                      type: 'gradient',
                      gradient: {
                        type: 'linear',
                        angle: g.angle || 135,
                        colors: [
                          { color: g.colors[0], position: 0 },
                          { color: g.colors[1], position: 100 }
                        ]
                      }
                    }
                  })
                }
              />
            </div>
          )}

          {canvasConfig.background.type === 'image' && (
            <ImageUploadField
              src={canvasConfig.background.image?.src || ''}
              onChangeSrc={(src) =>
                onUpdateCanvas({
                  background: {
                    type: 'image',
                    image: { src, fit: canvasConfig.background.image?.fit || 'cover', opacity: 1 }
                  }
                })
              }
            />
          )}
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Border" icon={<Square className="w-4 h-4" />}>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <InputLabel>Width</InputLabel>
              <NumericInput
                value={canvasConfig.border?.width || 0}
                onChange={(v) => onUpdateCanvas({ border: { ...canvasConfig.border!, width: v } })}
                fallback={0} min={0}
              />
            </div>
            <div>
              <InputLabel>Radius</InputLabel>
              <NumericInput
                value={canvasConfig.border?.radius || 0}
                onChange={(v) => onUpdateCanvas({ border: { ...canvasConfig.border!, radius: v } })}
                fallback={0} min={0}
              />
            </div>
          </div>
          <ColorPalette
            label="Color"
            value={canvasConfig.border?.color || '#E5E7EB'}
            onChange={(color) =>
              onUpdateCanvas({
                border: { ...canvasConfig.border!, color }
              })
            }
          />
        </div>
      </CollapsibleSection>

      {/* Decorative Borders — always available for editing */}
      <CollapsibleSection title="Template Borders" icon={<Layout className="w-4 h-4" />} defaultOpen={!!canvasConfig.decorativeBorder}>
        <div className="space-y-4">
          {/* Outer Border */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Outer Border</span>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!canvasConfig.decorativeBorder?.outer && canvasConfig.decorativeBorder.outer.enabled !== false}
                  onChange={(e) => {
                    const db = canvasConfig.decorativeBorder || {};
                    onUpdateCanvas({
                      decorativeBorder: {
                        ...db,
                        outer: db.outer
                          ? { ...db.outer, enabled: e.target.checked }
                          : e.target.checked
                            ? { enabled: true, width: 1, style: 'solid' as const, color: '#3B82F6', inset: { top: 25, left: 35, right: 35, bottom: 25 } }
                            : undefined
                      }
                    });
                  }}
                  className="w-3.5 h-3.5 rounded text-blue-600"
                />
                <span className="text-xs text-gray-500">Show</span>
              </label>
            </div>
            {canvasConfig.decorativeBorder?.outer?.enabled && (
              <div className="space-y-2 pl-1">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <InputLabel>Width</InputLabel>
                    <NumericInput value={canvasConfig.decorativeBorder.outer.width}
                      onChange={(v) => onUpdateCanvas({ decorativeBorder: { ...canvasConfig.decorativeBorder!, outer: { ...canvasConfig.decorativeBorder!.outer!, width: v } } })}
                      fallback={1} min={0} max={10}
                      className="w-full px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <InputLabel>Style</InputLabel>
                    <select value={canvasConfig.decorativeBorder.outer.style}
                      onChange={(e) => onUpdateCanvas({ decorativeBorder: { ...canvasConfig.decorativeBorder!, outer: { ...canvasConfig.decorativeBorder!.outer!, style: e.target.value as any } } })}
                      className="w-full px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-blue-500">
                      <option value="solid">Solid</option><option value="dashed">Dashed</option><option value="dotted">Dotted</option>
                    </select>
                  </div>
                </div>
                <ColorPalette
                  label="Color"
                  value={canvasConfig.decorativeBorder.outer.color}
                  onChange={(color) => onUpdateCanvas({ decorativeBorder: { ...canvasConfig.decorativeBorder!, outer: { ...canvasConfig.decorativeBorder!.outer!, color } } })}
                />
                <div>
                  <InputLabel>Inset (top / left / right / bottom)</InputLabel>
                  <div className="grid grid-cols-4 gap-1">
                    {(['top', 'left', 'right', 'bottom'] as const).map((side) => (
                      <NumericInput key={side}
                        value={canvasConfig.decorativeBorder!.outer!.inset[side]}
                        onChange={(v) => onUpdateCanvas({ decorativeBorder: { ...canvasConfig.decorativeBorder!, outer: { ...canvasConfig.decorativeBorder!.outer!, inset: { ...canvasConfig.decorativeBorder!.outer!.inset, [side]: v } } } })}
                        fallback={0} min={0} max={100}
                        className="w-full px-1.5 py-1 text-sm border rounded focus:ring-2 focus:ring-blue-500 text-center"
                        title={side} placeholder={side[0].toUpperCase()} />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Inner Border */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Inner Border</span>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!canvasConfig.decorativeBorder?.inner && canvasConfig.decorativeBorder.inner.enabled !== false}
                  onChange={(e) => {
                    const db = canvasConfig.decorativeBorder || {};
                    onUpdateCanvas({
                      decorativeBorder: {
                        ...db,
                        inner: db.inner
                          ? { ...db.inner, enabled: e.target.checked }
                          : e.target.checked
                            ? { enabled: true, width: 2, style: 'solid' as const, color: '#1E40AF', inset: { top: 35, left: 45, right: 45, bottom: 35 } }
                            : undefined
                      }
                    });
                  }}
                  className="w-3.5 h-3.5 rounded text-blue-600"
                />
                <span className="text-xs text-gray-500">Show</span>
              </label>
            </div>
            {canvasConfig.decorativeBorder?.inner?.enabled && (
              <div className="space-y-2 pl-1">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <InputLabel>Width</InputLabel>
                    <NumericInput value={canvasConfig.decorativeBorder.inner.width}
                      onChange={(v) => onUpdateCanvas({ decorativeBorder: { ...canvasConfig.decorativeBorder!, inner: { ...canvasConfig.decorativeBorder!.inner!, width: v } } })}
                      fallback={1} min={0} max={10}
                      className="w-full px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <InputLabel>Style</InputLabel>
                    <select value={canvasConfig.decorativeBorder.inner.style}
                      onChange={(e) => onUpdateCanvas({ decorativeBorder: { ...canvasConfig.decorativeBorder!, inner: { ...canvasConfig.decorativeBorder!.inner!, style: e.target.value as any } } })}
                      className="w-full px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-blue-500">
                      <option value="solid">Solid</option><option value="dashed">Dashed</option><option value="dotted">Dotted</option>
                    </select>
                  </div>
                </div>
                <ColorPalette
                  label="Color"
                  value={canvasConfig.decorativeBorder.inner.color}
                  onChange={(color) => onUpdateCanvas({ decorativeBorder: { ...canvasConfig.decorativeBorder!, inner: { ...canvasConfig.decorativeBorder!.inner!, color } } })}
                />
                <div>
                  <InputLabel>Inset (top / left / right / bottom)</InputLabel>
                  <div className="grid grid-cols-4 gap-1">
                    {(['top', 'left', 'right', 'bottom'] as const).map((side) => (
                      <NumericInput key={side}
                        value={canvasConfig.decorativeBorder!.inner!.inset[side]}
                        onChange={(v) => onUpdateCanvas({ decorativeBorder: { ...canvasConfig.decorativeBorder!, inner: { ...canvasConfig.decorativeBorder!.inner!, inset: { ...canvasConfig.decorativeBorder!.inner!.inset, [side]: v } } } })}
                        fallback={0} min={0} max={100}
                        className="w-full px-1.5 py-1 text-sm border rounded focus:ring-2 focus:ring-blue-500 text-center"
                        title={side} placeholder={side[0].toUpperCase()} />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Corner Decorations */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Corners</span>
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!canvasConfig.decorativeBorder?.corners && canvasConfig.decorativeBorder.corners.enabled !== false}
                  onChange={(e) => {
                    const db = canvasConfig.decorativeBorder || {};
                    onUpdateCanvas({
                      decorativeBorder: {
                        ...db,
                        corners: db.corners
                          ? { ...db.corners, enabled: e.target.checked }
                          : e.target.checked
                            ? { enabled: true, color: '#1E40AF', size: 15, width: 2, inset: { top: 43, left: 53, right: 53, bottom: 43 } }
                            : undefined
                      }
                    });
                  }}
                  className="w-3.5 h-3.5 rounded text-blue-600"
                />
                <span className="text-xs text-gray-500">Show</span>
              </label>
            </div>
            {canvasConfig.decorativeBorder?.corners?.enabled && (
              <div className="space-y-2 pl-1">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <InputLabel>Size</InputLabel>
                    <NumericInput value={canvasConfig.decorativeBorder.corners.size}
                      onChange={(v) => onUpdateCanvas({ decorativeBorder: { ...canvasConfig.decorativeBorder!, corners: { ...canvasConfig.decorativeBorder!.corners!, size: v } } })}
                      fallback={10} min={5} max={50}
                      className="w-full px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <InputLabel>Thickness</InputLabel>
                    <NumericInput value={canvasConfig.decorativeBorder.corners.width}
                      onChange={(v) => onUpdateCanvas({ decorativeBorder: { ...canvasConfig.decorativeBorder!, corners: { ...canvasConfig.decorativeBorder!.corners!, width: v } } })}
                      fallback={2} min={1} max={5}
                      className="w-full px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-blue-500" />
                  </div>
                </div>
                <ColorPalette
                  label="Color"
                  value={canvasConfig.decorativeBorder.corners.color}
                  onChange={(color) => onUpdateCanvas({ decorativeBorder: { ...canvasConfig.decorativeBorder!, corners: { ...canvasConfig.decorativeBorder!.corners!, color } } })}
                />
                <div>
                  <InputLabel>Inset (top / left / right / bottom)</InputLabel>
                  <div className="grid grid-cols-4 gap-1">
                    {(['top', 'left', 'right', 'bottom'] as const).map((side) => (
                      <NumericInput key={side}
                        value={canvasConfig.decorativeBorder!.corners!.inset[side]}
                        onChange={(v) => onUpdateCanvas({ decorativeBorder: { ...canvasConfig.decorativeBorder!, corners: { ...canvasConfig.decorativeBorder!.corners!, inset: { ...canvasConfig.decorativeBorder!.corners!.inset, [side]: v } } } })}
                        fallback={0} min={0} max={100}
                        className="w-full px-1.5 py-1 text-sm border rounded focus:ring-2 focus:ring-blue-500 text-center"
                        title={side} placeholder={side[0].toUpperCase()} />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </CollapsibleSection>
    </>
  );

  // Element properties panel
  const renderElementProperties = () => {
    if (!selectedElement) {
      return (
        <div className="flex-1 flex items-center justify-center text-gray-400 p-8">
          <div className="text-center">
            <Settings className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-sm">Select an element to edit its properties</p>
          </div>
        </div>
      );
    }

    return (
      <>
        {/* Element name */}
        <div className="px-4 py-3 border-b bg-gray-50">
          <InputLabel>Element Name</InputLabel>
          <input
            type="text"
            value={selectedElement.name}
            onChange={(e) => onUpdateElement(selectedElement.id, { name: e.target.value })}
            className="w-full px-2 py-1.5 text-sm border rounded focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Position & Size */}
        <CollapsibleSection title="Position & Size" icon={<Move className="w-4 h-4" />}>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <InputLabel>X</InputLabel>
              <NumericInput
                value={Math.round(selectedElement.position.x)}
                onChange={(v) => updatePosition('x', v)}
                fallback={0}
              />
            </div>
            <div>
              <InputLabel>Y</InputLabel>
              <NumericInput
                value={Math.round(selectedElement.position.y)}
                onChange={(v) => updatePosition('y', v)}
                fallback={0}
              />
            </div>
            <div>
              <InputLabel>Width</InputLabel>
              <NumericInput
                value={Math.round(selectedElement.position.width)}
                onChange={(v) => updatePosition('width', v)}
                fallback={50} min={1}
              />
            </div>
            <div>
              <InputLabel>Height</InputLabel>
              <NumericInput
                value={Math.round(selectedElement.position.height)}
                onChange={(v) => updatePosition('height', v)}
                fallback={20} min={1}
              />
            </div>
          </div>
          <div>
            <InputLabel>Rotation</InputLabel>
            <input
              type="range"
              min="-180"
              max="180"
              value={selectedElement.position.rotation || 0}
              onChange={(e) => updatePosition('rotation', parseInt(e.target.value))}
              className="w-full"
            />
            <div className="text-xs text-gray-500 text-center">
              {selectedElement.position.rotation || 0}°
            </div>
          </div>
          <div>
            <InputLabel>Opacity</InputLabel>
            <input
              type="range"
              min="0"
              max="100"
              value={(selectedElement.opacity ?? 1) * 100}
              onChange={(e) =>
                onUpdateElement(selectedElement.id, { opacity: parseInt(e.target.value) / 100 })
              }
              className="w-full"
            />
            <div className="text-xs text-gray-500 text-center">
              {Math.round((selectedElement.opacity ?? 1) * 100)}%
            </div>
          </div>
        </CollapsibleSection>

        {/* Text-specific properties */}
        {(selectedElement.type === CanvasElementType.TEXT ||
          selectedElement.type === CanvasElementType.VARIABLE) && (
          <>
            <CollapsibleSection title="Typography" icon={<Type className="w-4 h-4" />}>
              <div className="space-y-3">
                {selectedElement.type === CanvasElementType.TEXT && (
                  <div>
                    <InputLabel>Content</InputLabel>
                    <textarea
                      value={(selectedElement as TextCanvasElement).content}
                      onChange={(e) =>
                        onUpdateElement(selectedElement.id, { content: e.target.value } as any)
                      }
                      rows={2}
                      className="w-full px-2 py-1.5 text-sm border rounded focus:ring-2 focus:ring-blue-500 resize-none"
                    />
                  </div>
                )}

                {selectedElement.type === CanvasElementType.VARIABLE && (
                  <>
                    <div>
                      <InputLabel>Variable Key</InputLabel>
                      <input
                        type="text"
                        value={(selectedElement as VariableCanvasElement).variableKey}
                        onChange={(e) =>
                          onUpdateElement(selectedElement.id, { variableKey: e.target.value } as any)
                        }
                        className="w-full px-2 py-1.5 text-sm border rounded focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <InputLabel>Placeholder</InputLabel>
                      <input
                        type="text"
                        value={(selectedElement as VariableCanvasElement).placeholder}
                        onChange={(e) =>
                          onUpdateElement(selectedElement.id, { placeholder: e.target.value } as any)
                        }
                        className="w-full px-2 py-1.5 text-sm border rounded focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </>
                )}

                <div>
                  <InputLabel>Font Family</InputLabel>
                  <select
                    value={
                      (selectedElement as TextCanvasElement | VariableCanvasElement).style.fontFamily
                    }
                    onChange={(e) => updateElementStyle({ fontFamily: e.target.value })}
                    className="w-full px-2 py-1.5 text-sm border rounded focus:ring-2 focus:ring-blue-500"
                  >
                    {FONT_FAMILIES.map((font) => (
                      <option key={font.value} value={font.value}>
                        {font.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <InputLabel>Size</InputLabel>
                    <NumericInput
                      value={(selectedElement as TextCanvasElement | VariableCanvasElement).style.fontSize}
                      onChange={(v) => updateElementStyle({ fontSize: v })}
                      fallback={16} min={1}
                    />
                  </div>
                  <div>
                    <InputLabel>Line Height</InputLabel>
                    <NumericInput
                      value={(selectedElement as TextCanvasElement | VariableCanvasElement).style.lineHeight}
                      onChange={(v) => updateElementStyle({ lineHeight: v })}
                      fallback={1.4} min={0.5} max={5} step="0.1" useFloat
                    />
                  </div>
                </div>

                {/* Text formatting buttons */}
                <div>
                  <InputLabel>Style</InputLabel>
                  <div className="flex gap-1">
                    <button
                      onClick={() =>
                        updateElementStyle({
                          fontWeight:
                            (selectedElement as TextCanvasElement).style.fontWeight === 'bold'
                              ? 'normal'
                              : 'bold'
                        })
                      }
                      className={`p-2 rounded border transition-colors ${
                        (selectedElement as TextCanvasElement).style.fontWeight === 'bold'
                          ? 'bg-gray-100 border-gray-400'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <Bold className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() =>
                        updateElementStyle({
                          fontStyle:
                            (selectedElement as TextCanvasElement).style.fontStyle === 'italic'
                              ? 'normal'
                              : 'italic'
                        })
                      }
                      className={`p-2 rounded border transition-colors ${
                        (selectedElement as TextCanvasElement).style.fontStyle === 'italic'
                          ? 'bg-gray-100 border-gray-400'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <Italic className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() =>
                        updateElementStyle({
                          textDecoration:
                            (selectedElement as TextCanvasElement).style.textDecoration ===
                            'underline'
                              ? 'none'
                              : 'underline'
                        })
                      }
                      className={`p-2 rounded border transition-colors ${
                        (selectedElement as TextCanvasElement).style.textDecoration === 'underline'
                          ? 'bg-gray-100 border-gray-400'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <Underline className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Text alignment */}
                <div>
                  <InputLabel>Alignment</InputLabel>
                  <div className="flex gap-1">
                    {[
                      { value: 'left', icon: AlignLeft },
                      { value: 'center', icon: AlignCenter },
                      { value: 'right', icon: AlignRight }
                    ].map(({ value, icon: Icon }) => (
                      <button
                        key={value}
                        onClick={() => updateElementStyle({ textAlign: value as TextAlign })}
                        className={`p-2 rounded border transition-colors ${
                          (selectedElement as TextCanvasElement).style.textAlign === value
                            ? 'bg-gray-100 border-gray-400'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </CollapsibleSection>

            <CollapsibleSection title="Colors" icon={<Palette className="w-4 h-4" />}>
              <ColorPalette
                label="Text Color"
                value={(selectedElement as TextCanvasElement).style.color}
                onChange={(color) => updateElementStyle({ color })}
              />
              <ColorPalette
                label="Background Color"
                value={(selectedElement as TextCanvasElement).style.backgroundColor || '#ffffff'}
                onChange={(color) => updateElementStyle({ backgroundColor: color === '#ffffff' ? undefined : color })}
              />
              {/* Rounds the background, so one text box can be a badge (half the height = pill) */}
              {selectedElement.type === CanvasElementType.TEXT && (
                <div>
                  <InputLabel>Corner Radius</InputLabel>
                  <NumericInput
                    value={(selectedElement as TextCanvasElement).style.borderRadius || 0}
                    onChange={(v) => updateElementStyle({ borderRadius: v })}
                    fallback={0} min={0} max={200}
                    title="Rounds the background corners. Half the box height makes a pill shape."
                  />
                </div>
              )}
            </CollapsibleSection>
          </>
        )}

        {/* Image-specific properties */}
        {selectedElement.type === CanvasElementType.IMAGE && (
          <CollapsibleSection title="Image" icon={<ImageIcon className="w-4 h-4" />}>
            <div className="space-y-3">
              <ImageUploadField
                src={(selectedElement as ImageCanvasElement).src}
                onChangeSrc={(src) => onUpdateElement(selectedElement.id, { src } as any)}
              />
              <div>
                <InputLabel>Fit</InputLabel>
                <select
                  value={(selectedElement as ImageCanvasElement).fit}
                  onChange={(e) =>
                    onUpdateElement(selectedElement.id, { fit: e.target.value } as any)
                  }
                  className="w-full px-2 py-1.5 text-sm border rounded focus:ring-2 focus:ring-blue-500"
                >
                  <option value="contain">Contain</option>
                  <option value="cover">Cover</option>
                  <option value="fill">Fill</option>
                  <option value="none">None</option>
                </select>
              </div>
            </div>
          </CollapsibleSection>
        )}

        {/* Shape-specific properties */}
        {selectedElement.type === CanvasElementType.SHAPE && (
          <CollapsibleSection title="Shape" icon={<Square className="w-4 h-4" />}>
            <div className="space-y-3">
              <div>
                <InputLabel>Fill Color</InputLabel>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={(selectedElement as ShapeCanvasElement).fill}
                    onChange={(e) =>
                      onUpdateElement(selectedElement.id, { fill: e.target.value } as any)
                    }
                    className="w-10 h-8 border rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={(selectedElement as ShapeCanvasElement).fill}
                    onChange={(e) =>
                      onUpdateElement(selectedElement.id, { fill: e.target.value } as any)
                    }
                    className="flex-1 px-2 py-1.5 text-sm border rounded focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div>
                <InputLabel>Border Radius</InputLabel>
                <NumericInput
                  value={(selectedElement as ShapeCanvasElement).border?.radius || 0}
                  onChange={(v) =>
                    onUpdateElement(selectedElement.id, {
                      border: {
                        ...(selectedElement as ShapeCanvasElement).border!,
                        radius: v
                      }
                    } as any)
                  }
                  fallback={0} min={0}
                />
              </div>
            </div>
          </CollapsibleSection>
        )}

        {/* Line-specific properties */}
        {selectedElement.type === CanvasElementType.LINE && (
          <CollapsibleSection title="Line" icon={<Square className="w-4 h-4" />}>
            <div className="space-y-3">
              <div>
                <InputLabel>Stroke Color</InputLabel>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={(selectedElement as LineCanvasElement).strokeColor}
                    onChange={(e) =>
                      onUpdateElement(selectedElement.id, { strokeColor: e.target.value } as any)
                    }
                    className="w-10 h-8 border rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={(selectedElement as LineCanvasElement).strokeColor}
                    onChange={(e) =>
                      onUpdateElement(selectedElement.id, { strokeColor: e.target.value } as any)
                    }
                    className="flex-1 px-2 py-1.5 text-sm border rounded focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div>
                <InputLabel>Stroke Width</InputLabel>
                <NumericInput
                  value={(selectedElement as LineCanvasElement).strokeWidth}
                  onChange={(v) =>
                    onUpdateElement(selectedElement.id, { strokeWidth: v } as any)
                  }
                  fallback={1} min={1}
                />
              </div>
              <div>
                <InputLabel>Style</InputLabel>
                <select
                  value={(selectedElement as LineCanvasElement).strokeStyle}
                  onChange={(e) =>
                    onUpdateElement(selectedElement.id, { strokeStyle: e.target.value } as any)
                  }
                  className="w-full px-2 py-1.5 text-sm border rounded focus:ring-2 focus:ring-blue-500"
                >
                  <option value="solid">Solid</option>
                  <option value="dashed">Dashed</option>
                  <option value="dotted">Dotted</option>
                </select>
              </div>
            </div>
          </CollapsibleSection>
        )}
      </>
    );
  };

  return (
    <div className="w-72 bg-white border-l flex flex-col h-full">
      {/* Tab Header */}
      <div className="flex-shrink-0 border-b">
        <div className="flex">
          <button
            onClick={() => setActiveTab('element')}
            className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${
              activeTab === 'element'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Element
          </button>
          <button
            onClick={() => setActiveTab('canvas')}
            className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${
              activeTab === 'canvas'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Canvas
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'canvas' ? renderCanvasProperties() : renderElementProperties()}
      </div>
    </div>
  );
}
